import React from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

/** Page chrome shared by the admin screens: title, optional action, table frame. */
export const AdminPage = ({ title, subtitle, action, children }) => (
  <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 4 }}>
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ font: `600 18px ${font.sans}`, color: color.text }}>{title}</Box>
          {subtitle && (
            <Box sx={{ mt: 0.5, font: `400 12.5px ${font.sans}`, color: color.textMuted }}>
              {subtitle}
            </Box>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  </Box>
);

export const AdminTable = ({ columns, rows, loading, empty }) => {
  const grid = columns.map((c) => c.width || "1fr").join(" ");

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ py: 5, textAlign: "center", font: `400 13px ${font.sans}`, color: color.textSecondary }}>
        {empty}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: `${radius.card}px`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: grid,
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${color.borderRow}`,
          bgcolor: color.bg,
        }}
      >
        {columns.map((c) => (
          <Box
            key={c.key}
            sx={{
              font: `600 10.5px ${font.sans}`,
              color: color.textMuted,
              letterSpacing: ".05em",
              textTransform: "uppercase",
            }}
          >
            {c.label}
          </Box>
        ))}
      </Box>

      {rows.map((row, i) => (
        <Box
          key={row.id || i}
          sx={{
            display: "grid",
            gridTemplateColumns: grid,
            gap: 1.5,
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: i === rows.length - 1 ? "none" : `1px solid ${color.borderRow}`,
            "&:hover": { bgcolor: color.rowTint },
          }}
        >
          {columns.map((c) => (
            <Box
              key={c.key}
              sx={{
                font: `400 12.5px ${font.sans}`,
                color: color.text,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {c.render ? c.render(row) : row[c.key] || "—"}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export const RowButton = (props) => (
  <Button
    size="small"
    variant="outlined"
    {...props}
    sx={{ font: `500 11.5px ${font.sans}`, py: 0.375, ...props.sx }}
  />
);
